import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { SiteHeader } from "../components/SiteHeader";
import { api } from "../lib/api";
import {
  Sparkles, ArrowRight, Shield, Globe, UploadCloud, Video, X, Loader2,
  Lightbulb, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

export default function HomePage() {
  const [userInput, setUserInput] = useState("");
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showStickyCta, setShowStickyCta] = useState(false);
  const [gdprConsent, setGdprConsent] = useState(false);
  const inputRef = useRef(null);
  const uploadCardRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setShowStickyCta(true), 1200);
    return () => clearTimeout(t);
  }, []);

  const scrollToUpload = () => {
    uploadCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith("video/")) {
      toast.error("Please select a video file");
      return;
    }
    if (f.size > 200 * 1024 * 1024) {
      toast.error("Video exceeds 200MB limit");
      return;
    }
    setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!file || !userInput.trim()) {
      toast.error("Describe your problem and choose a video");
      return;
    }
    if (!gdprConsent) {
      toast.error("Please accept the privacy statement to start the analysis.");
      return;
    }
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append("user_input", userInput.trim());
      form.append("file", file);
      form.append("gdpr_consent", "true");
      const { data } = await api.post("/submissions", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploading complete");
      navigate(`/results/${data.id}`);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Upload failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />
      <main className="max-w-3xl mx-auto px-6 py-16" data-testid="homepage-main">
        <div className="mb-10">
          <p className="eyebrow mb-3 text-[#065F46]" data-testid="hero-eyebrow">
            AI-powered golf swing analysis
          </p>
          <h1 className="font-display font-bold tracking-tight text-4xl sm:text-5xl text-slate-900 leading-[1.05] mb-5">
            Upload your swing.<br />
            Instant <span className="text-[#065F46]">AI swing feedback</span>.
          </h1>
          <p className="text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed mb-6">
            Personalized drills and swing fixes in seconds — no gear, no apps, no signup. Upload, see your score, unlock the full breakdown for $2.99.
          </p>
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-50 border border-emerald-200" data-testid="price-pill">
            <Sparkles className="w-3.5 h-3.5 text-[#065F46]" />
            <span className="text-sm font-medium text-[#065F46]">Free swing score · Full analysis $2.99 · No signup</span>
          </div>
        </div>

        <Card ref={uploadCardRef} className="border-slate-200 shadow-sm p-6 sm:p-8" data-testid="upload-card">
          <form onSubmit={onSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="user-input" className="eyebrow">
                What is your problem?
              </Label>
              <Textarea
                id="user-input"
                data-testid="user-input-textarea"
                required
                rows={4}
                placeholder="e.g. slice, bad balance, topping the ball"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                className="resize-none"
              />
              <p className="text-xs text-slate-500 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-[#065F46]" />
                The AI uses this and the swing video to generate your feedback.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="eyebrow">Swing video</Label>
              {!file ? (
                <div
                  data-testid="upload-video-dropzone"
                  onClick={() => inputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={onDrop}
                  className={`border-2 border-dashed rounded-lg p-10 sm:p-12 text-center cursor-pointer transition-colors ${
                    isDragging
                      ? "border-[#065F46] bg-emerald-50/50"
                      : "border-slate-300 bg-slate-50 hover:border-[#065F46]"
                  }`}
                >
                  <UploadCloud className="w-10 h-10 mx-auto text-slate-400 mb-3" />
                  <p className="text-sm font-medium text-slate-900">
                    Drop your video here, or click to browse
                  </p>
                  <p className="text-xs text-slate-500 mt-1">MP4, MOV, WEBM · up to 200MB · deleted after analysis</p>
                  <input
                    ref={inputRef}
                    data-testid="file-input"
                    type="file"
                    accept="video/*"
                    className="hidden"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                  />
                </div>
              ) : (
                <div
                  data-testid="file-preview"
                  className="flex items-center justify-between gap-3 border border-slate-200 rounded-lg p-4 bg-slate-50"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-10 h-10 rounded-md bg-[#065F46] text-white flex items-center justify-center shrink-0">
                      <Video className="w-5 h-5" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{file.name}</p>
                      <p className="text-xs text-slate-500">{(file.size / (1024 * 1024)).toFixed(1)} MB</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    data-testid="remove-file-button"
                    onClick={() => setFile(null)}
                    className="text-slate-400 hover:text-slate-900 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>

            {/* Upload tips */}
            <div
              data-testid="upload-tips"
              className="rounded-lg border border-slate-200 bg-slate-50/60 p-4 sm:p-5"
            >
              <div className="flex items-start gap-3 mb-3">
                <span className="w-7 h-7 rounded-md bg-emerald-100 text-[#065F46] flex items-center justify-center shrink-0">
                  <Lightbulb className="w-4 h-4" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-slate-900">For best results</p>
                  <p className="text-xs text-slate-500">A few quick tips before you upload.</p>
                </div>
              </div>
              <ul className="space-y-1.5 text-sm text-slate-700 pl-10" data-testid="upload-tips-list">
                <li className="flex items-baseline gap-2">
                  <span className="w-1 h-1 rounded-full bg-[#065F46] shrink-0 translate-y-[-3px]" />
                  Upload videos in normal speed
                </li>
                <li className="flex items-baseline gap-2">
                  <span className="w-1 h-1 rounded-full bg-[#065F46] shrink-0 translate-y-[-3px]" />
                  Make sure the full swing is visible
                </li>
                <li className="flex items-baseline gap-2">
                  <span className="w-1 h-1 rounded-full bg-[#065F46] shrink-0 translate-y-[-3px]" />
                  Use good lighting
                </li>
                <li className="flex items-baseline gap-2">
                  <span className="w-1 h-1 rounded-full bg-[#065F46] shrink-0 translate-y-[-3px]" />
                  Record from face-on or down-the-line
                </li>
              </ul>

              <div className="mt-4 pt-3 border-t border-slate-200/80">
                <div className="flex items-start gap-3 mb-2">
                  <span className="w-7 h-7 rounded-md bg-amber-100 text-[#B45309] flex items-center justify-center shrink-0">
                    <AlertCircle className="w-4 h-4" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">If analysis is not working, try</p>
                  </div>
                </div>
                <ul className="space-y-1.5 text-sm text-slate-700 pl-10" data-testid="upload-troubleshoot-list">
                  <li className="flex items-baseline gap-2">
                    <span className="w-1 h-1 rounded-full bg-[#B45309] shrink-0 translate-y-[-3px]" />
                    Uploading a shorter video
                  </li>
                  <li className="flex items-baseline gap-2">
                    <span className="w-1 h-1 rounded-full bg-[#B45309] shrink-0 translate-y-[-3px]" />
                    Uploading a smaller file
                  </li>
                  <li className="flex items-baseline gap-2">
                    <span className="w-1 h-1 rounded-full bg-[#B45309] shrink-0 translate-y-[-3px]" />
                    Using a normal-speed video instead of slow motion
                  </li>
                </ul>
              </div>
            </div>

            {/* GDPR consent — required before analysis can start */}
            <div
              data-testid="gdpr-consent-row"
              className="flex items-start gap-3 rounded-lg border border-slate-200 bg-emerald-50/40 p-4"
            >
              <Checkbox
                id="gdpr-consent"
                data-testid="gdpr-consent-checkbox"
                checked={gdprConsent}
                onCheckedChange={(v) => setGdprConsent(Boolean(v))}
                className="mt-0.5 border-slate-400 data-[state=checked]:bg-[#065F46] data-[state=checked]:border-[#065F46]"
              />
              <Label
                htmlFor="gdpr-consent"
                className="text-sm font-normal leading-relaxed text-slate-700 cursor-pointer select-none"
                data-testid="gdpr-consent-label"
              >
                By starting the analysis, you agree that your video and text are sent
                to our AI for analysis. We do not store any personal data or video
                files after the analysis is complete.{" "}
                <Link
                  to="/privacy"
                  data-testid="gdpr-consent-policy-link"
                  className="text-[#065F46] underline hover:no-underline font-medium"
                >
                  Read our privacy policy
                </Link>
                .
              </Label>
            </div>

            <Button
              type="submit"
              data-testid="submit-upload-button"
              disabled={submitting || !file || !userInput.trim() || !gdprConsent}
              className="w-full h-11 bg-[#065F46] hover:bg-[#047857] text-white text-sm font-semibold"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading…
                </>
              ) : (
                <>
                  Analyze swing
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
            <p className="text-xs text-slate-400 text-center">
              No signup required. Free swing score on the next page · unlock the full analysis for $2.99.
            </p>
          </form>
        </Card>

        {/* Steps */}
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6" data-testid="steps-section">
          {[
            { n: "01", t: "Upload", d: "Drop your swing clip and describe the problem." },
            { n: "02", t: "Free preview", d: "Get your swing score and main fault instantly." },
            { n: "03", t: "Unlock $2.99", d: "Get a 10-15 min practice session, one swing thought and a checklist for the range." },
          ].map((s) => (
            <div key={s.n}>
              <p className="font-display text-3xl text-[#065F46] font-semibold mb-1">{s.n}</p>
              <p className="font-semibold text-slate-900">{s.t}</p>
              <p className="text-sm text-slate-600">{s.d}</p>
            </div>
          ))}
        </div>

        {/* FAQ */}
        <section className="mt-20" data-testid="faq-section">
          <div className="mb-8">
            <p className="eyebrow mb-2 text-[#065F46]">Frequently asked</p>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Everything you might be wondering</h2>
          </div>
          <Card className="border-slate-200 shadow-none p-2 sm:p-3">
            <Accordion type="single" collapsible className="w-full">
              {[
                { q: "Will this work for my swing?", a: "Yes. GPT-4o looks at frames from your swing video and combines that with the problem you describe to give you a fix tailored to your specific issue. Works for right- and left-handed golfers, drivers, irons, wedges, even putters." },
                { q: "Do I need to create an account?", a: "No. Upload, see your free swing score, and pay $2.99 only if you want the full breakdown. No email or sign-up required. Save the result link if you want to come back later." },
                { q: "How fast do I get feedback?", a: "Usually under 10 seconds for the AI to generate your free preview. The full breakdown unlocks the moment you complete the $2.99 checkout." },
                { q: "What happens to my video?", a: "We extract a handful of still frames, send them to OpenAI's GPT-4o, then discard the video. We never store the full video file." },
                { q: "What if I'm a beginner?", a: "Beginners get the most out of clear, focused feedback because their mistakes are usually fundamental (grip, posture, tempo). Just describe the problem in plain English — \"the ball goes everywhere\" works." },
              ].map((item, i) => (
                <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`} className="px-3 sm:px-4 border-slate-200 last:border-b-0">
                  <AccordionTrigger className="text-left font-medium text-slate-900 hover:no-underline py-5 text-[15px]">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-slate-600 leading-relaxed text-[15px] pb-5 pr-4">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Card>
          <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-1 text-xs text-slate-500">
            <p data-testid="faq-guarantee">Not happy with your analysis? Save your link and reply to support within 7 days — we&apos;ll refund you, no questions asked.</p>
            <p className="text-slate-400">$2.99 · One-time · No subscription</p>
          </div>
        </section>

        <div className="mt-20 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-slate-500" data-testid="trust-strip">
          <span className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5 text-slate-400" /> Secure checkout by Stripe</span>
          <span className="flex items-center gap-1.5"><Globe className="w-3.5 h-3.5 text-slate-400" /> International cards accepted</span>
          <span className="flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-slate-400" /> Apple Pay · Google Pay · Link</span>
        </div>

        <footer
          data-testid="site-footer"
          className="mt-12 pt-8 border-t border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs text-slate-500"
        >
          <p>&copy; {new Date().getFullYear()} SwingShape &mdash; operated by Awarak AB.</p>
          <div className="flex items-center gap-5">
            <Link
              to="/privacy"
              data-testid="footer-privacy-link"
              className="hover:text-[#065F46] hover:underline"
            >
              Privacy policy
            </Link>
            <a
              href="mailto:support@swingshape.se"
              data-testid="footer-contact-email"
              className="hover:text-[#065F46] hover:underline"
            >
              Contact
            </a>
          </div>
        </footer>
      </main>

      {/* Mobile sticky CTA */}
      <div
        data-testid="sticky-cta-bar"
        className={`md:hidden fixed inset-x-0 bottom-0 z-30 transition-transform duration-300 ease-out ${
          showStickyCta ? "translate-y-0" : "translate-y-full"
        }`}
      >
        <div className="bg-white/95 backdrop-blur-sm border-t border-slate-200 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-[0_-4px_20px_-8px_rgba(15,23,42,0.08)]">
          <Button
            data-testid="sticky-cta-button"
            onClick={scrollToUpload}
            className="w-full h-12 bg-[#065F46] hover:bg-[#047857] text-white font-semibold text-sm rounded-lg"
          >
            Analyze My Swing · Free Preview
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
